import { Injectable, inject } from '@angular/core';
import {
  BsApiService,
  BsCacheService,
  GenericResponseCode,
  SimpleGlobalModalService,
} from '@brightside-web/desktop/data-access/core-services';
import { Router } from '@angular/router';
import { BehaviorSubject, from, Observable, of, Subject, switchMap } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { SavingsAccount } from '@brightside-web/desktop/feature/savings-account';
import {
  FinancialAccountsCreationService,
  FinancialAccountTypes,
  KYCResponseOrigin,
} from '@brightside-web/desktop/feature/financial-accounts-core';
import { DocumentsKeyEnum, LegalDocumentsService } from '@brightside-web/shared/legal-documents';

export interface LoanPayload {
  payload: {
    loans: LoanPostStatusResponse[];
    partner: LoanPartner;
  };
}

export interface LoanPartner {
  type: string;
  link: string;
  displayStyle: string;
  displayName: string;
  active: boolean;
  legalDocumentsKey: string;
  fundsSourceType: string;
  canCreateLoan: boolean;
  phoneNumber: string;
}

export interface LoanPostPayload {
  payload: {
    account: SavingsAccount;
    loan: LoanPostStatusResponse;
  };
}

export interface LoanPostStatusResponse {
  isTaken: boolean;
  referenceId: string;
  status: LoanStatus;
  statusDisplayName: string;
  inProgress: boolean;
  partner: LoanPartner;
  remainingBalance: number;
  paymentAmount: number;
  loanTerm: number;
  loanAmount: number;
  apr: number;
  loanEndDate: string;
  lastUpdated: string;
  approvalDate: string;
  nextPaymentDate: string;
}

export enum LoanStatus {
  LOAN_APPROVED = 'LOAN_APPROVED',
  LOAN_PENDING = 'LOAN_PENDING',
  LOAN_DECLINED = 'LOAN_DECLINED',
  ACCOUNT_CREATED = 'ACCOUNT_CREATED',
  LOAN_NON_REPAYABLE_BANKRUPTCY = 'LOAN_NON_REPAYABLE_BANKRUPTCY',
  LOAN_FUNDING_FAILED = 'LOAN_FUNDING_FAILED',
  LOAN_PROCESS_PENDING = 'LOAN_PROCESS_PENDING',
  LOAN_PROCESS_ERROR = 'LOAN_PROCESS_ERROR',
  LOAN_FUNDED = 'LOAN_FUNDED',
  LOAN_DENIED = 'LOAN_DENIED',
  LOAN_NOT_APPROVED = 'LOAN_NOT_APPROVED',
  LOAN_NON_REPAYABLE = 'LOAN_NON_REPAYABLE',
  LOAN_PAID_OFF = 'LOAN_PAID_OFF',
  LOAN_OFFERED = 'LOAN_OFFERED',
  APPLICATION_TECHNICAL_ISSUE = 'APPLICATION_TECHNICAL_ISSUE',
  USER_ACTION_REQUIRED = 'USER_ACTION_REQUIRED',
  APPLICATION_REVIEW = 'APPLICATION_REVIEW',
  LOAN_ACCEPTED = 'LOAN_ACCEPTED',
}

export enum LoanSteps {
  INTRO = 'intro',
  AUTOSAVE = 'autosave',
  HIGHLIGHTS = 'highlights',
  INFO = 'info',
  CREATE = 'create',
}

@Injectable()
export class NewLoansService {
  apiName = 'api-mobile';

  _cachedLoanInfo: LoanPostStatusResponse | undefined;
  _cachedCurrentPartnerInfo: LoanPartner;
  hasSavings: boolean;
  hasAutosave: boolean;

  private _partnerInfo = new Subject<LoanPartner>();
  public readonly partnerInfo: Observable<LoanPartner> = this._partnerInfo.asObservable();

  private _loansInfo = new Subject<LoanPostStatusResponse[]>();
  public readonly loansInfo: Observable<LoanPostStatusResponse[]> = this._loansInfo.asObservable();

  private _currentStep = new BehaviorSubject<LoanSteps>(LoanSteps.INTRO);
  public readonly currentStep: Observable<LoanSteps> = this._currentStep.asObservable();

  private _loanCallDone = new BehaviorSubject<boolean>(false);
  public readonly loanCallDone: Observable<boolean> = this._loanCallDone.asObservable();


  private _bsApi: BsApiService = inject(BsApiService);
  private _globalModalSvc: SimpleGlobalModalService  = inject(SimpleGlobalModalService);
  private _router: Router  = inject(Router);
  private _faCreateSvc: FinancialAccountsCreationService  = inject(FinancialAccountsCreationService);
  private _legalDocumentsService: LegalDocumentsService  = inject(LegalDocumentsService);
  private _bsCacheService: BsCacheService  = inject(BsCacheService);
  
  constructor() {
    this._faCreateSvc.accountType = FinancialAccountTypes.LOANS;
  }

  getLoanByPartnerType(partnerType?: string): Observable<LoanPayload | boolean> {
    return from(this._bsCacheService.getItem('loanVendorFromCard')).pipe(
      switchMap((loanVendorFromCard) => {
        partnerType = partnerType ?? loanVendorFromCard;
        if (partnerType) {
          return this._bsApi.get<LoanPayload>(this.apiName, `/loans-app-api?partnerType=${partnerType}`).pipe(
            take(1),
            tap((resp) => {
              this._partnerInfo.next(resp.payload.partner);
              this._cachedCurrentPartnerInfo = resp.payload.partner;
              this._loansInfo.next(resp.payload.loans);
              this.currentInProgressLoan(resp.payload.loans);
              this._loanCallDone.next(true);
            })
          );
        }
        return of(false);
      })
    );
  }

  postLoan() {
    if (this._cachedCurrentPartnerInfo) {
      this._bsApi
        .post<LoanPostPayload>(this.apiName, '/loans-app-api', {
          partnerType: this._cachedCurrentPartnerInfo.type,
        })
        .pipe(
          tap((resp) => {
            this._faCreateSvc.kycResponseOrigin = KYCResponseOrigin.ACCOUNT_CREATION;
            this._faCreateSvc.checkResultCode(resp.result.code);
          }),
          catchError((err: Error) => {
            const msg = err.message.toLowerCase();
            this._faCreateSvc.sharedErrors(msg);
            return from([]);
          })
        )
        .subscribe((response) => {
          if (response.result.code === GenericResponseCode.SERVICE_OK) {
            if (!this.hasSavings) {
              this._globalModalSvc.createModal({
                title: 'SAVINGS_SUCCESS_TITLE',
                contentKey: 'SAVINGS_SUCCESS_DESC',
                displayCloseButton: false,
                eventName: 'loan_flow_confirmation',
                isError: false,
                size: 'medium',
                ctaKey: 'SAVINGS_SUCCESS_CTA',
                ctaAction: () => {
                  this._router.navigate(['accounts', 'loans', 'creation', 'autosave-select']);
                },
              });
            } else {
              if (response.payload.account.fundsSource?.autoSaveSchedule.status === 'active') {
                this._router.navigate(['accounts', 'loans', 'info']);
              } else {
                this._router.navigate(['accounts', 'loans', 'creation', 'autosave-select']);
              }
            }
          }
        });
    }
  }

  private currentInProgressLoan(loans: LoanPostStatusResponse[]) {
    this._cachedLoanInfo = loans.find((loan) => loan.inProgress);
  }

  setStep(step: LoanSteps) {
    this._currentStep.next(step);
  }

  doesLoanHaveDocs(): Observable<boolean | DocumentsKeyEnum> {
    const legalDocsKey = this._cachedCurrentPartnerInfo.legalDocumentsKey as DocumentsKeyEnum;
    return this._legalDocumentsService.getDocsByKey(legalDocsKey).pipe(
      map((docs) => {
        if (docs.payload?.legalDocuments && docs.payload.legalDocuments.length > 0) {
          return legalDocsKey as DocumentsKeyEnum;
        } else {
          return false;
        }
      })
    );
  }
}
